import {
  AirPlayButton,
  BufferingIndicator,
  CaptionsButton,
  CastButton,
  Controls,
  FullscreenButton,
  Gesture,
  Hotkey,
  Menu,
  MuteButton,
  PiPButton,
  PlayButton,
  Poster,
  StatusAnnouncer,
  Time,
  TimeSlider,
  Tooltip,
} from "@videojs/react";
import {
  AirPlayEnterIcon,
  AirPlayExitIcon,
  CaptionsOffIcon,
  CaptionsOnIcon,
  CastEnterIcon,
  CastExitIcon,
  FullscreenEnterIcon,
  FullscreenExitIcon,
  GearIcon,
  PauseIcon,
  PipEnterIcon,
  PipExitIcon,
  PlayIcon,
  RestartIcon,
  SpinnerIcon,
  VolumeHighIcon,
  VolumeLowIcon,
  VolumeOffIcon,
} from "@videojs/react/icons/minimal";
import { forwardRef, type ComponentProps } from "react";

import type { ChapterBlock } from "../../types/VideoTypes";
import { player } from "./Player";
import VideoMenu from "./VideoMenu";
import VideoChapters from "./VideoChapters";

interface VideoPlayerSkinProps {
  poster?: string;
  posterAlt?: string;
  children: React.ReactNode;
  chapters?: ChapterBlock[];
}

interface ControlTooltipProps {
  children: NonNullable<ComponentProps<typeof Tooltip.Trigger>["render"]>;
}

const iconClassName = "size-5";

const ControlButton = forwardRef<HTMLButtonElement, ComponentProps<"button">>(
  function ControlButton({ className = "", ...props }, ref) {
    return (
      <button
        ref={ref}
        type="button"
        className={`grid size-10 shrink-0 cursor-pointer place-items-center rounded-md border-0 bg-transparent p-0 text-white outline-2 outline-transparent transition-colors hover:bg-white/15 focus-visible:bg-white/15 focus-visible:outline-white ${className}`}
        {...props}
      />
    );
  }
);

function ControlTooltip({ children }: Readonly<ControlTooltipProps>) {
  return (
    <Tooltip.Root side="top">
      <Tooltip.Trigger render={children} />
      <Tooltip.Popup className="z-30 rounded-md border border-white/10 bg-black/80 px-2 py-1 text-xs text-white shadow-md backdrop-blur-md">
        <Tooltip.Label />
        <Tooltip.Shortcut className="ml-2 text-white/60" />
      </Tooltip.Popup>
    </Tooltip.Root>
  );
}

export default function VideoPlayerSkin({
  poster,
  posterAlt,
  chapters,
  children,
}: Readonly<VideoPlayerSkinProps>) {
  const hasChapters = chapters && chapters.length > 0;

  return (
    <player.Container className="group/skin relative aspect-video w-full overflow-hidden rounded-lg bg-black text-white outline-none [&_video]:block [&_video]:size-full [&_video]:object-contain [&:fullscreen]:rounded-none">
      {children}

      {poster ? (
        <Poster
          src={poster}
          alt={posterAlt}
          className="pointer-events-none absolute inset-0 size-full rounded-[inherit] object-cover transition-opacity duration-250 not-data-visible:opacity-0 [&[data-visible]:not([data-loaded])]:opacity-0"
        />
      ) : null}

      <BufferingIndicator
        render={(indicatorProps) => (
          <div
            {...indicatorProps}
            className="pointer-events-none absolute inset-0 z-10 hidden place-items-center data-visible:grid"
          >
            <SpinnerIcon className="size-10 animate-spin text-white drop-shadow-md motion-reduce:animate-none" />
          </div>
        )}
      />

      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-2/3 bg-linear-to-t from-black/80 via-black/20 to-transparent" />

      <Controls.Root
        data-controls=""
        className="absolute inset-x-1 bottom-1 z-20 flex flex-wrap items-center gap-x-1 rounded-lg p-1 text-white transition-[translate,opacity] duration-150 not-data-visible:pointer-events-none not-data-visible:translate-y-full not-data-visible:opacity-0 sm:flex-nowrap"
      >
        <Tooltip.Provider>
          <div className="flex flex-1 items-center sm:flex-none">
            {/* Play button */}
            <ControlTooltip>
              <PlayButton className="group" render={<ControlButton />}>
                <RestartIcon
                  className={`${iconClassName} hidden group-data-ended:block`}
                />
                <PlayIcon
                  className={`${iconClassName} hidden group-not-data-ended:group-not-data-started:block group-not-data-ended:group-data-paused:block`}
                />
                <PauseIcon
                  className={`${iconClassName} hidden group-data-started:group-not-data-paused:group-not-data-ended:block`}
                />
              </PlayButton>
            </ControlTooltip>

            {/* Volume button */}
            <ControlTooltip>
              <MuteButton className="group" render={<ControlButton />}>
                <VolumeOffIcon
                  className={`${iconClassName} hidden group-data-muted:block`}
                />
                <VolumeLowIcon
                  className={`${iconClassName} hidden group-not-data-muted:group-data-[volume-level=low]:block`}
                />
                <VolumeHighIcon
                  className={`${iconClassName} hidden group-not-data-muted:group-not-data-[volume-level=low]:block`}
                />
              </MuteButton>
            </ControlTooltip>
          </div>

          <div className="order-first flex basis-full items-center gap-2 px-2 sm:order-none sm:min-w-32 sm:flex-1 sm:basis-auto">
            {/* Time display */}
            <Time.Group className="shrink-0 text-xs tabular-nums">
              <Time.Value type="current" />
              <Time.Separator className="px-1 text-white/60" />
              <Time.Value type="duration" />
            </Time.Group>

            {/* Time slider */}
            <TimeSlider.Root className="group/slider relative flex h-8 min-w-20 flex-1 cursor-pointer items-center rounded-full outline-none">
              <TimeSlider.Track className="relative h-1 w-full overflow-hidden rounded-full bg-white/25 ring-1 ring-black/10">
                <TimeSlider.Buffer className="absolute inset-y-0 left-0 w-(--media-slider-buffer) rounded-full bg-white/20 transition-[width]" />
                <TimeSlider.Fill className="absolute inset-y-0 left-0 w-(--media-slider-fill) rounded-full bg-white group-data-dragging/slider:w-(--media-slider-pointer)" />
              </TimeSlider.Track>
              {hasChapters && <VideoChapters chapters={chapters} />}
            </TimeSlider.Root>
          </div>

          <div className="flex flex-1 items-center justify-end sm:flex-none">
            {/* Airplay is only available in Safari */}
            <ControlTooltip>
              <AirPlayButton className="group" render={<ControlButton />}>
                <AirPlayEnterIcon
                  className={`${iconClassName} hidden group-data-[airplay-state=disconnected]:block`}
                />
                <AirPlayExitIcon
                  className={`${iconClassName} hidden group-data-[airplay-state=connected]:block`}
                />
              </AirPlayButton>
            </ControlTooltip>

            {/* Google Cast is only available in Chromium browsers */}
            <ControlTooltip>
              <CastButton
                className="group data-disabled:cursor-not-allowed data-disabled:opacity-50"
                render={<ControlButton />}
              >
                <CastEnterIcon
                  className={`${iconClassName} hidden group-not-data-[cast-state=connected]:block`}
                />
                <CastExitIcon
                  className={`${iconClassName} hidden group-data-[cast-state=connected]:block`}
                />
              </CastButton>
            </ControlTooltip>

            {/* Captions */}
            <ControlTooltip>
              <CaptionsButton className="group" render={<ControlButton />}>
                <CaptionsOffIcon
                  className={`${iconClassName} hidden group-not-data-active:block`}
                />
                <CaptionsOnIcon
                  className={`${iconClassName} hidden group-data-active:block`}
                />
              </CaptionsButton>
            </ControlTooltip>

            {/* Settings */}
            <ControlTooltip>
              <Menu.Root side="top">
                <Menu.Trigger render={<ControlButton aria-label="Settings" />}>
                  <GearIcon className={`cursor-pointer ${iconClassName}`} />
                </Menu.Trigger>
                <VideoMenu />
              </Menu.Root>
            </ControlTooltip>

            {/* Picture-in-picture */}
            <ControlTooltip>
              <PiPButton
                className="group hidden sm:grid"
                render={<ControlButton />}
              >
                <PipEnterIcon
                  className={`${iconClassName} hidden group-not-data-pip:block`}
                />
                <PipExitIcon
                  className={`${iconClassName} hidden group-data-pip:block`}
                />
              </PiPButton>
            </ControlTooltip>

            {/* Full screen */}
            <ControlTooltip>
              <FullscreenButton className="group" render={<ControlButton />}>
                <FullscreenEnterIcon
                  className={`${iconClassName} hidden group-not-data-fullscreen:block`}
                />
                <FullscreenExitIcon
                  className={`${iconClassName} hidden group-data-fullscreen:block`}
                />
              </FullscreenButton>
            </ControlTooltip>
          </div>
        </Tooltip.Provider>
      </Controls.Root>

      <Hotkey keys="Space" action="togglePaused" />
      <Hotkey keys="k" action="togglePaused" />
      <Hotkey keys="m" action="toggleMuted" />
      <Hotkey keys="f" action="toggleFullscreen" />
      <Hotkey keys="c" action="toggleSubtitles" />
      <Hotkey keys="i" action="togglePictureInPicture" />
      <Hotkey keys="ArrowRight" action="seekStep" value={5} />
      <Hotkey keys="ArrowLeft" action="seekStep" value={-5} />

      <Gesture
        type="tap"
        action="togglePaused"
        pointer="mouse"
        region="center"
      />
      <Gesture type="tap" action="toggleControls" pointer="touch" />
      <Gesture type="doubletap" action="seekStep" value={-10} region="left" />
      <Gesture type="doubletap" action="toggleFullscreen" region="center" />
      <Gesture type="doubletap" action="seekStep" value={10} region="right" />

      <StatusAnnouncer className="sr-only" />
    </player.Container>
  );
}
